import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import PartnerCreateDrawer from '../components/PartnerCreateDrawer';
import PartnerDetailDrawer from '../components/drawers/PartnerDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { Building2, Search, Mail, ExternalLink, MapPin, Users, Gift, Sparkles, Eye, EyeOff, RefreshCw, Filter, Download } from 'lucide-react';
import { allCategories, aiInsights, AIInsight } from '../utils/partnersData';
const Partners = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {
    partners
  } = useAdminData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Tutte');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [globalInsights, setGlobalInsights] = useState<AIInsight[]>(aiInsights.filter(insight => !insight.partnerId || insight.partnerId === 0));
  // Check if we should open the drawer from navigation state or URL params
  useEffect(() => {
    if (location.state?.openDrawer) {
      setShowCreateDrawer(true);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
    // Check for partner ID in URL params (from /partners/:partnerId route)
    if (params.partnerId) {
      setSelectedPartnerId(params.partnerId);
      setShowDetailDrawer(true);
    }
  }, [location, params.partnerId]);
  // Filter partners with useMemo
  const filteredPartners = useMemo(() => {
    return partners.filter(partner => {
      const matchesSearch = partner.name.toLowerCase().includes(searchQuery.toLowerCase()) || partner.description.toLowerCase().includes(searchQuery.toLowerCase()) || partner.categories.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'Tutte' || partner.categories.includes(selectedCategory);
      const matchesStatus = statusFilter === 'all' || partner.status === statusFilter;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [partners, searchQuery, selectedCategory, statusFilter]);
  const handleSendBulkEmail = () => {
    alert(`Email inviata a ${filteredPartners.length} partner!\n\nOggetto: ${emailSubject}\n\nMessaggio: ${emailMessage}`);
    setShowEmailModal(false);
    setEmailSubject('');
    setEmailMessage('');
  };
  const handleAddPartner = () => {
    setShowCreateDrawer(true);
  };
  const handlePartnerClick = (partnerId: string) => {
    setSelectedPartnerId(partnerId);
    setShowDetailDrawer(true);
  };
  const handleCloseDetailDrawer = () => {
    setShowDetailDrawer(false);
    setTimeout(() => {
      setSelectedPartnerId(null);
      // Clear URL parameter by navigating to base partners route
      navigate('/partners', {
        replace: true
      });
    }, 300);
  };
  const toggleInsightVisibility = (insightId: string) => {
    setGlobalInsights(globalInsights.map(insight => insight.id === insightId ? {
      ...insight,
      visible: !insight.visible
    } : insight));
  };
  const regenerateInsight = (insightId: string) => {
    setGlobalInsights(globalInsights.map(insight => insight.id === insightId ? {
      ...insight,
      generatedAt: new Date().toISOString(),
      description: insight.description + ' [Rigenerato]'
    } : insight));
  };
  const getInsightIcon = (type: AIInsight['type']) => {
    switch (type) {
      case 'opportunity':
        return <Sparkles className="h-5 w-5 text-green-600" data-id="element-2841" />;
      case 'warning':
        return <Sparkles className="h-5 w-5 text-yellow-600" data-id="element-2842" />;
      case 'trend':
        return <Sparkles className="h-5 w-5 text-blue-600" data-id="element-2843" />;
      case 'recommendation':
        return <Sparkles className="h-5 w-5 text-purple-600" data-id="element-2844" />;
    }
  };
  const getInsightBgColor = (type: AIInsight['type']) => {
    switch (type) {
      case 'opportunity':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'trend':
        return 'bg-blue-50 border-blue-200';
      case 'recommendation':
        return 'bg-purple-50 border-purple-200';
    }
  };
  // Calculate total stats
  const totalLocations = partners.reduce((sum, p) => sum + (p.locations || 0), 0);
  const totalUsers = partners.reduce((sum, p) => sum + (p.activeUsers || 0), 0);
  const totalRedemptions = partners.reduce((sum, p) => sum + (p.totalRedemptions || 0), 0);
  return <div className="flex h-screen bg-gray-50" data-id="element-2845">
      <div className="w-20 flex-shrink-0" data-id="element-2846">
        <Sidebar onAddPartner={handleAddPartner} data-id="element-2847" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2848">
        <Topbar data-id="element-2849" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-2850">
          <PageHeader title="Partners" subtitle="Gestisci i partner della piattaforma Greenback" primaryAction={{
          label: 'Aggiungi Partner',
          onClick: handleAddPartner,
          icon: <Building2 className="h-4 w-4 mr-2" data-id="element-2852" />
        }} secondaryAction={{
          label: 'Esporta CSV',
          onClick: () => console.log('Export CSV'),
          icon: <Download className="h-4 w-4 mr-2" data-id="element-2853" />
        }} data-id="element-2851" />

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-id="element-2854">
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2855">
              <div className="flex items-center justify-between mb-2" data-id="element-2856">
                <Building2 className="h-5 w-5 text-gray-400" data-id="element-2857" />
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-2858">
                {partners.length}
              </p>
              <p className="text-sm text-gray-600" data-id="element-2859">Partner totali</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2860">
              <div className="flex items-center justify-between mb-2" data-id="element-2861">
                <MapPin className="h-5 w-5 text-gray-400" data-id="element-2862" />
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-2863">
                {totalLocations}
              </p>
              <p className="text-sm text-gray-600" data-id="element-2864">Sedi totali</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2865">
              <div className="flex items-center justify-between mb-2" data-id="element-2866">
                <Users className="h-5 w-5 text-gray-400" data-id="element-2867" />
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-2868">
                {totalUsers.toLocaleString('it-IT')}
              </p>
              <p className="text-sm text-gray-600" data-id="element-2869">Utenti attivi</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2870">
              <div className="flex items-center justify-between mb-2" data-id="element-2871">
                <Gift className="h-5 w-5 text-gray-400" data-id="element-2872" />
              </div>
              <p className="text-2xl font-bold text-gray-900" data-id="element-2873">
                {totalRedemptions.toLocaleString('it-IT')}
              </p>
              <p className="text-sm text-gray-600" data-id="element-2874">Riscatti totali</p>
            </div>
          </div>

          {/* AI Insights Section */}
          {globalInsights.length > 0 && <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6" data-id="element-2875">
              <div className="flex items-center gap-2 mb-4" data-id="element-2876">
                <Sparkles className="h-5 w-5 text-green-600" data-id="element-2877" />
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-2878">
                  AI Insights Globali
                </h2>
              </div>
              <div className="space-y-3" data-id="element-2879">
                {globalInsights.filter(insight => insight.visible).map(insight => <div key={insight.id} className={`border rounded-lg p-4 ${getInsightBgColor(insight.type)}`} data-id="element-2880">
                      <div className="flex items-start justify-between mb-2" data-id="element-2881">
                        <div className="flex items-center gap-2" data-id="element-2882">
                          {getInsightIcon(insight.type)}
                          <h3 className="font-semibold text-gray-900 text-sm" data-id="element-2883">
                            {insight.title}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-2884">
                          <button onClick={() => regenerateInsight(insight.id)} className="p-1 hover:bg-white/50 rounded transition-colors" title="Rigenera insight" data-id="element-2885">
                            <RefreshCw className="h-4 w-4 text-gray-600" data-id="element-2886" />
                          </button>
                          <button onClick={() => toggleInsightVisibility(insight.id)} className="p-1 hover:bg-white/50 rounded transition-colors" title="Nascondi insight" data-id="element-2887">
                            <EyeOff className="h-4 w-4 text-gray-600" data-id="element-2888" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700" data-id="element-2889">
                        {insight.description}
                      </p>
                    </div>)}
              </div>
            </div>}

          {/* Filters and Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6" data-id="element-2890">
            <div className="flex flex-col md:flex-row gap-4" data-id="element-2891">
              {/* Search */}
              <div className="flex-1 relative" data-id="element-2892">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2893" />
                <input type="text" placeholder="Cerca partner per nome, descrizione o categoria..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-2894" />
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-2" data-id="element-2895">
                <Filter className="h-5 w-5 text-gray-400" data-id="element-2896" />
                <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-2897">
                  <option value="Tutte" data-id="element-2898">Tutte le categorie</option>
                  {allCategories.map(category => <option key={category} value={category} data-id="element-2899">
                      {category}
                    </option>)}
                </select>
              </div>

              {/* Status Filter */}
              <div className="flex items-center gap-2" data-id="element-2900">
                <Filter className="h-5 w-5 text-gray-400" data-id="element-2901" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-2902">
                  <option value="all" data-id="element-2903">Tutti gli stati</option>
                  <option value="active" data-id="element-2904">Attivo</option>
                  <option value="inactive" data-id="element-2905">Inattivo</option>
                  <option value="pending" data-id="element-2906">In Attesa</option>
                </select>
              </div>

              {/* Bulk Email Button */}
              <button onClick={() => setShowEmailModal(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap" data-id="element-2907">
                <Mail className="h-4 w-4" data-id="element-2908" />
                Invia Email a Tutti
              </button>
            </div>

            {/* Active Filters Display */}
            {(searchQuery || selectedCategory !== 'Tutte' || statusFilter !== 'all') && <div className="mt-3 flex items-center gap-2 text-sm text-gray-600" data-id="element-2909">
                <span className="font-medium" data-id="element-2910">Filtri attivi:</span>
                {searchQuery && <span className="px-2 py-1 bg-gray-100 rounded" data-id="element-2911">
                    Ricerca: "{searchQuery}"
                  </span>}
                {selectedCategory !== 'Tutte' && <span className="px-2 py-1 bg-gray-100 rounded" data-id="element-2912">
                    Categoria: {selectedCategory}
                  </span>}
                {statusFilter !== 'all' && <span className="px-2 py-1 bg-gray-100 rounded" data-id="element-2913">
                    Stato: {statusFilter}
                  </span>}
                <button onClick={() => {
              setSearchQuery('');
              setSelectedCategory('Tutte');
              setStatusFilter('all');
            }} className="text-green-600 hover:underline ml-2" data-id="element-2914">
                  Rimuovi filtri
                </button>
              </div>}
          </div>

          {/* Partners Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2915">
            {filteredPartners.map(partner => <div key={partner.id} className="bg-white rounded-lg border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handlePartnerClick(partner.id)} data-id="element-2916">
                <div className="p-6" data-id="element-2917">
                  {/* Logo and Name */}
                  <div className="flex items-start gap-4 mb-4" data-id="element-2918">
                    <div className="h-16 w-16 rounded-lg border border-gray-200 bg-white flex items-center justify-center p-3 flex-shrink-0" data-id="element-2919">
                      <img src={partner.logo} alt={partner.name} className="max-h-full max-w-full object-contain" onError={e => {
                    e.currentTarget.src = 'https://via.placeholder.com/64?text=Logo';
                  }} data-id="element-2920" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2921">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate" data-id="element-2922">
                        {partner.name}
                      </h3>
                      <a href={partner.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700 transition-colors" data-id="element-2923">
                        <ExternalLink className="h-3 w-3" data-id="element-2924" />
                        <span className="truncate" data-id="element-2925">Visita sito</span>
                      </a>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2" data-id="element-2926">
                    {partner.description}
                  </p>

                  {/* Categories */}
                  <div className="flex flex-wrap gap-2 mb-4" data-id="element-2927">
                    {partner.categories.slice(0, 3).map((category, index) => <span key={index} className="px-2 py-1 bg-green-600/10 text-green-600 text-xs font-medium rounded-full" data-id="element-2928">
                        {category}
                      </span>)}
                    {partner.categories.length > 3 && <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full" data-id="element-2929">
                        +{partner.categories.length - 3}
                      </span>}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100" data-id="element-2930">
                    <div className="text-center" data-id="element-2931">
                      <p className="text-lg font-bold text-gray-900" data-id="element-2932">
                        {partner.locations}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2933">Sedi</p>
                    </div>
                    <div className="text-center" data-id="element-2934">
                      <p className="text-lg font-bold text-gray-900" data-id="element-2935">
                        {partner.activeRewards}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2936">Premi</p>
                    </div>
                    <div className="text-center" data-id="element-2937">
                      <p className="text-lg font-bold text-gray-900" data-id="element-2938">
                        {partner.totalRedemptions?.toLocaleString('it-IT')}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2939">Riscatti</p>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>

          {/* No Results */}
          {filteredPartners.length === 0 && <div className="bg-white rounded-lg border border-gray-200 p-12 text-center" data-id="element-2940">
              <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-2941" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-2942">
                Nessun partner trovato
              </h3>
              <p className="text-gray-500 mb-4" data-id="element-2943">
                Prova a modificare i filtri di ricerca
              </p>
              <button onClick={() => {
            setSearchQuery('');
            setSelectedCategory('Tutte');
            setStatusFilter('all');
          }} className="text-green-600 hover:underline" data-id="element-2944">
                Rimuovi tutti i filtri
              </button>
            </div>}
        </main>
      </div>

      {/* Bulk Email Modal */}
      {showEmailModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-2945">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6" data-id="element-2946">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2947">
              Invia Email a Tutti i Partner
            </h2>
            <p className="text-gray-600 mb-6" data-id="element-2948">
              Stai per inviare un'email a {filteredPartners.length} partner
              {selectedCategory !== 'Tutte' && ` nella categoria "${selectedCategory}"`}
              .
            </p>

            <div className="space-y-4 mb-6" data-id="element-2949">
              <div data-id="element-2950">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2951">
                  Oggetto
                </label>
                <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Inserisci l'oggetto dell'email..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-2952" />
              </div>

              <div data-id="element-2953">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2954">
                  Messaggio
                </label>
                <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} placeholder="Scrivi il messaggio dell'email..." rows={6} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none" data-id="element-2955" />
              </div>
            </div>

            <div className="flex gap-3 justify-end" data-id="element-2956">
              <button onClick={() => {
            setShowEmailModal(false);
            setEmailSubject('');
            setEmailMessage('');
          }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2957">
                Annulla
              </button>
              <button onClick={handleSendBulkEmail} disabled={!emailSubject || !emailMessage} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2958">
                Invia Email
              </button>
            </div>
          </div>
        </div>}

      {/* Partner Create Drawer */}
      <PartnerCreateDrawer isOpen={showCreateDrawer} onClose={() => setShowCreateDrawer(false)} onSuccess={() => {
      console.log('Partner created successfully');
    }} data-id="element-2959" />

      {/* Partner Detail Drawer */}
      <PartnerDetailDrawer isOpen={showDetailDrawer} onClose={handleCloseDetailDrawer} partnerId={selectedPartnerId} data-id="element-2960" />
    </div>;
};
export default Partners;